<template>
  <div class="faq-page">
    <section class="faq-header">
      <h1>{{ $t('website.faq.faq') }}</h1>
      <p>{{ $t('website.faq.faq_description') }}</p>
    </section>

    <section class="faq-category-quickselect">
      <h2>{{ $t('website.faq.categories') }}</h2>
      <div class="faq-category-quickselect-buttons">
        <button
          v-for="category in faqCategories"
          :key="category.title"
          @click="scrollTo(category.title)"
        >
          {{ category.title }}
        </button>
      </div>
    </section>

    <section class="faq-categories">
      <div v-for="category in faqCategories" :key="category.title">
        <div class="faq-category" :id="category.title">
          <h2>{{ category.title }}</h2>
          <div v-for="question in category.questions" :key="question.question">
            <div class="faq-question">
              <h3>{{ question.question }}</h3>
              <p>{{ question.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: 'website',
});

const { t, locale } = useI18n();

import logo from '@/assets/logo/chronofox_wortbild_right.png';
useSeoMeta({
  // General
  title: t('website.faq.faq') + ' | ' + t('app_name'),
  description: t('website.faq.meta_faq_description'),
  keywords: `${t('website.faq.meta_keywords')}`,

  // Open Graph
  ogTitle: t('app_name') + ' - ' + t('website.faq.faq'),
  ogDescription: t('website.faq.meta_faq_description'),
  ogLocale: locale.value ? locale.value : 'de',
  ogType: 'website',
  ogImage: logo,

  // Twitter (X)
  twitterCard: 'summary_large_image',
  twitterTitle: t('website.faq.faq') + ' | ' + t('app_name'),
  twitterDescription: t('website.faq.meta_faq_description'),
  twitterImage: logo,
  twitterSite: '@ChronoFoxApp',
});

const scrollTo = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    const offset = 100; // Adjust this value as needed
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - offset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

const faqCategories = [
  {
    title: t('website.faq.getting_started'),
    questions: [
      {
        question: t('website.faq.what_is_chronofox'),
        answer: t('website.faq.what_is_chronofox_answer'),
      },
      {
        question: t('website.faq.how_to_sign_up'),
        answer: t('website.faq.how_to_sign_up_answer'),
      },
      {
        question: t('website.faq.is_chronofox_free'),
        answer: t('website.faq.is_chronofox_free_answer'),
      },
    ],
  },
  {
    title: t('website.faq.features'),
    questions: [
      {
        question: t('website.faq.how_does_chronofox_track_time'),
        answer: t('website.faq.how_does_chronofox_track_time_answer'),
      },
      {
        question: t('website.faq.can_i_manage_multiple_clients'),
        answer: t('website.faq.can_i_manage_multiple_clients_answer'),
      },
      {
        question: t('website.faq.can_i_customize_hourly_rates'),
        answer: t('website.faq.can_i_customize_hourly_rates_answer'),
      },
      {
        question: t('website.faq.does_chronofox_support_team_collaboration'),
        answer: t(
          'website.faq.does_chronofox_support_team_collaboration_answer'
        ),
      },
      {
        question: t('website.faq.can_i_generate_reports'),
        answer: t('website.faq.can_i_generate_reports_answer'),
      },
    ],
  },
  {
    title: t('website.faq.technical'),
    questions: [
      {
        question: t('website.faq.what_platforms_are_supported'),
        answer: t('website.faq.what_platforms_are_supported_answer'),
      },
      {
        question: t('website.faq.is_my_data_secure'),
        answer: t('website.faq.is_my_data_secure_answer'),
      },
      {
        question: t('website.faq.can_i_use_chronofox_offline'),
        answer: t('website.faq.can_i_use_chronofox_offline_answer'),
      },
    ],
  },
  {
    title: t('website.faq.billing'),
    questions: [
      {
        question: t('website.faq.what_are_subscription_options'),
        answer: t('website.faq.what_are_subscription_options_answer'),
      },
      {
        question: t('website.faq.how_to_cancel_subscription'),
        answer: t('website.faq.how_to_cancel_subscription_answer'),
      },
      {
        question: t('website.faq.are_there_discounts'),
        answer: t('website.faq.are_there_discounts_answer'),
      },
    ],
  },
  {
    title: t('website.faq.support'),
    questions: [
      {
        question: t('website.faq.how_to_contact_support'),
        answer: t('website.faq.how_to_contact_support_answer'),
      },
      {
        question: t('website.faq.what_to_do_if_password_forgotten'),
        answer: t('website.faq.what_to_do_if_password_forgotten_answer'),
      },
      {
        question: t('website.faq.can_i_transfer_data'),
        answer: t('website.faq.can_i_transfer_data_answer'),
      },
    ],
  },
];
</script>
<style scoped>
.faq-page {
  padding: 2rem 5%;
  margin: 0 auto;
  text-align: left;

  display: flex;
  flex-direction: column;
  align-items: center;
}

.faq-header {
  text-align: center;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 1200px;
}

.faq-header h1 {
  font-size: 3rem;
  font-weight: bold;
  color: #333;
}

.faq-header p {
  font-size: 1.2rem;
  color: #777;
  margin-top: 0.5rem;
}

/* FAQ category quickselect */
.faq-category-quickselect {
  margin-top: 3rem;
  width: 100%;
  max-width: 1200px;
}

.faq-category-quickselect h2 {
  font-size: 2rem;
  color: #333;
  margin-bottom: 1rem;
}

.faq-category-quickselect-buttons {
  display: flex;
  gap: 1rem;
  justify-content: flex-start;
}

.faq-category-quickselect-buttons button {
  background: #f9f9f9;
  border: 1px solid var(--t-accent);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 1.2rem;
  color: #555;
  cursor: pointer;
  transition: background 0.3s;
}

.faq-category-quickselect-buttons button:hover {
  background: #f0f0f0;
}

/* FAQ categories list with question */
.faq-categories {
  margin-top: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
}

.faq-category {
  background: #f9f9f9;
  padding: 1.5rem;
}

.faq-category h2 {
  font-size: 1.8rem;
  color: #444;
  border-bottom: 2px solid var(--t-accent);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.faq-question {
  margin-top: 1.5rem;
}

.faq-question h3 {
  font-size: 1.5rem;
  color: #333;
  cursor: pointer;
  transition: color 0.3s;
}

.faq-question p {
  font-size: 1.2rem;
  color: #555;
  line-height: 1.5;
  margin-top: 0.5rem;
}

@media (max-width: 768px) {
  .faq-category-quickselect-buttons {
    flex-direction: column;
  }

  .faq-category-quickselect-buttons button {
    margin-bottom: 1rem;
  }
}
</style>
